// Flood-fill / select-by-color helper used by the Magic Wand tool.
//
// Layer: 1 (Pixel Core). MUST remain DOM-free / browser-free.
//
// Two operations:
//   - floodFillFromSeed: contiguous 4-way flood from a seed pixel using a
//     color-similarity test.
//   - selectByColor:     non-contiguous match — every pixel within tolerance
//     of the seed color, anywhere in the buffer.
//
// Color distance metric: **max-channel absolute difference** of RGB —
//   d = max(|dR|, |dG|, |dB|).
// We compare `d <= tolerance`. This is the cheaper Photoshop-style metric
// (a per-channel L∞ ball) — clicking a pixel and bumping tolerance by 32
// includes anything where every channel is within 32 of the seed, matching
// most users' intuition. Manhattan / Euclidean would dilate diagonal color
// directions and feel less predictable. Alpha is not part of the metric;
// see the transparent-seed special case below.
//
// Transparent-seed behavior: when the seed pixel has alpha === 0, we match
// *only* other alpha === 0 pixels (RGB is undefined for fully transparent
// pixels, so comparing it is meaningless). This makes clicking the empty
// area around an opaque object select the transparent region.
//
// For an opaque seed, transparent pixels (alpha === 0) are *never* matched
// regardless of RGB distance — they belong to the transparent region.
//
// Anti-aliasing: when `antiAlias` is on, we first do the binary flood, then
// soften boundary pixels (a 4-connected neighbor is outside the region) by
// dropping their mask value to a linear ramp keyed on color distance:
//   d <= tol/2  → 255
//   d >= tol    → keep boundary as 255 — interior never softened
// Concretely: at boundary pixels we set mask to
//   round(255 * clamp((tol - d) / (tol/2), 0, 1))
// so the closer-to-seed boundary stays opaque, the farther-from-seed boundary
// fades toward 0. Interior pixels are untouched (stay 255).

export interface FloodFillOptions {
  /** 0..255 max-channel tolerance. */
  tolerance: number;
  /** true = only the connected region containing the seed; false = entire layer. */
  contiguous: boolean;
  /** Soft mask edges at the boundary. */
  antiAlias: boolean;
  /** For orchestrator awareness; the tool decides how to use it. */
  sampleAllLayers: boolean;
}

/**
 * Contiguous 4-way flood fill from (seedX, seedY). Returns a layer-sized
 * mask where 255 = inside region, 0 = outside, 1..254 = AA edge (when
 * `antiAlias` is true).
 *
 * Out-of-bounds seed returns an all-zero mask. Pixels outside the buffer
 * are treated as outside the region (no implicit "match through the edge").
 */
export function floodFillFromSeed(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  seedX: number,
  seedY: number,
  opts: FloodFillOptions,
): Uint8Array {
  const n = width * height;
  const mask = new Uint8Array(n);
  if (width <= 0 || height <= 0) return mask;
  if (seedX < 0 || seedY < 0 || seedX >= width || seedY >= height) return mask;
  if (pixels.length < n * 4) return mask;

  const sx = seedX | 0;
  const sy = seedY | 0;
  const sIdx = (sy * width + sx) * 4;
  const sR = pixels[sIdx]!;
  const sG = pixels[sIdx + 1]!;
  const sB = pixels[sIdx + 2]!;
  const sA = pixels[sIdx + 3]!;
  const transparentMode = sA === 0;
  const tol = clampTol(opts.tolerance);

  // Queue: flat typed-array of pixel indices (0..n-1). We use head/tail.
  // Worst case the queue holds every pixel once, so we size it to n.
  const queue = new Int32Array(n);
  let qHead = 0;
  let qTail = 0;

  const seedPix = sy * width + sx;
  if (!matches(pixels, seedPix, sR, sG, sB, sA, transparentMode, tol)) {
    // Even the seed doesn't match (shouldn't happen, but guard anyway).
    return mask;
  }
  mask[seedPix] = 255;
  queue[qTail++] = seedPix;

  while (qHead < qTail) {
    const p = queue[qHead++]!;
    const px = p % width;
    const py = (p / width) | 0;
    // 4-neighbors.
    if (px > 0) {
      const np = p - 1;
      if (mask[np] === 0 && matches(pixels, np, sR, sG, sB, sA, transparentMode, tol)) {
        mask[np] = 255;
        queue[qTail++] = np;
      }
    }
    if (px < width - 1) {
      const np = p + 1;
      if (mask[np] === 0 && matches(pixels, np, sR, sG, sB, sA, transparentMode, tol)) {
        mask[np] = 255;
        queue[qTail++] = np;
      }
    }
    if (py > 0) {
      const np = p - width;
      if (mask[np] === 0 && matches(pixels, np, sR, sG, sB, sA, transparentMode, tol)) {
        mask[np] = 255;
        queue[qTail++] = np;
      }
    }
    if (py < height - 1) {
      const np = p + width;
      if (mask[np] === 0 && matches(pixels, np, sR, sG, sB, sA, transparentMode, tol)) {
        mask[np] = 255;
        queue[qTail++] = np;
      }
    }
  }

  if (opts.contiguous === false) {
    // Caller asked for non-contiguous via floodFillFromSeed. We extend the
    // mask to include every matching pixel anywhere in the buffer. This
    // overrides the contiguous walk above with the broader set — interior
    // pixels stay 255 and AA still applies to the new boundaries.
    fillAllMatching(pixels, width, height, mask, sR, sG, sB, sA, transparentMode, tol);
  }

  if (opts.antiAlias && !transparentMode) {
    applyEdgeAntiAlias(pixels, width, height, mask, sR, sG, sB, tol);
  }

  return mask;
}

/**
 * Non-contiguous select-by-color: every pixel in the buffer whose color is
 * within `tolerance` of (seedR, seedG, seedB). If `seedR/G/B` come from a
 * transparent seed pixel, callers should instead build an alpha=0 mask via
 * the contiguous=false path of floodFillFromSeed — this helper assumes an
 * opaque target color.
 */
export function selectByColor(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  seedR: number,
  seedG: number,
  seedB: number,
  opts: Omit<FloodFillOptions, "contiguous">,
): Uint8Array {
  const n = width * height;
  const mask = new Uint8Array(n);
  if (width <= 0 || height <= 0) return mask;
  if (pixels.length < n * 4) return mask;
  const tol = clampTol(opts.tolerance);
  const sR = seedR & 0xff;
  const sG = seedG & 0xff;
  const sB = seedB & 0xff;

  fillAllMatching(pixels, width, height, mask, sR, sG, sB, 255, false, tol);
  if (opts.antiAlias) {
    applyEdgeAntiAlias(pixels, width, height, mask, sR, sG, sB, tol);
  }
  return mask;
}

// =================== internals ===================

function clampTol(t: number): number {
  if (!Number.isFinite(t)) return 0;
  if (t < 0) return 0;
  if (t > 255) return 255;
  return t | 0;
}

function matches(
  pixels: Uint8ClampedArray,
  pixIdx: number,
  sR: number,
  sG: number,
  sB: number,
  _sA: number,
  transparentMode: boolean,
  tol: number,
): boolean {
  const i = pixIdx * 4;
  const a = pixels[i + 3]!;
  if (transparentMode) {
    return a === 0;
  }
  // Opaque seed: never include fully transparent pixels.
  if (a === 0) return false;
  const r = pixels[i]!;
  const g = pixels[i + 1]!;
  const b = pixels[i + 2]!;
  const dR = r > sR ? r - sR : sR - r;
  const dG = g > sG ? g - sG : sG - g;
  const dB = b > sB ? b - sB : sB - b;
  const d = dR > dG ? (dR > dB ? dR : dB) : dG > dB ? dG : dB;
  return d <= tol;
}

function fillAllMatching(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  mask: Uint8Array,
  sR: number,
  sG: number,
  sB: number,
  sA: number,
  transparentMode: boolean,
  tol: number,
): void {
  const n = width * height;
  for (let p = 0; p < n; p++) {
    if (mask[p] !== 0) continue;
    if (matches(pixels, p, sR, sG, sB, sA, transparentMode, tol)) {
      mask[p] = 255;
    }
  }
}

// Color distance of pixel `pixIdx` from the seed RGB. Returns max-channel L∞.
function colorDist(
  pixels: Uint8ClampedArray,
  pixIdx: number,
  sR: number,
  sG: number,
  sB: number,
): number {
  const i = pixIdx * 4;
  const r = pixels[i]!;
  const g = pixels[i + 1]!;
  const b = pixels[i + 2]!;
  const dR = r > sR ? r - sR : sR - r;
  const dG = g > sG ? g - sG : sG - g;
  const dB = b > sB ? b - sB : sB - b;
  return dR > dG ? (dR > dB ? dR : dB) : dG > dB ? dG : dB;
}

// For each boundary pixel (in the region, with at least one 4-neighbor that
// is *not* in the region or is out-of-buffer), drop its mask value based on
// color distance:
//   d <= tol/2 → 255 (unchanged)
//   d >= tol   → 0 (still in mask conceptually, but boundary fades to none)
//   between   → linear ramp.
// Interior pixels are unchanged. Out-of-buffer neighbors count as outside.
function applyEdgeAntiAlias(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  mask: Uint8Array,
  sR: number,
  sG: number,
  sB: number,
  tol: number,
): void {
  if (tol <= 0) return; // No AA range to interpolate over.
  const half = tol / 2;
  const denom = tol - half; // = tol/2, always > 0 here.
  const n = width * height;
  // First, identify boundary pixels (snapshot to avoid in-place feedback).
  const boundary = new Uint8Array(n);
  for (let p = 0; p < n; p++) {
    if (mask[p] === 0) continue;
    const px = p % width;
    const py = (p / width) | 0;
    let isBoundary = false;
    if (px === 0 || mask[p - 1] === 0) isBoundary = true;
    else if (px === width - 1 || mask[p + 1] === 0) isBoundary = true;
    else if (py === 0 || mask[p - width] === 0) isBoundary = true;
    else if (py === height - 1 || mask[p + width] === 0) isBoundary = true;
    if (isBoundary) boundary[p] = 1;
  }
  for (let p = 0; p < n; p++) {
    if (boundary[p] === 0) continue;
    const d = colorDist(pixels, p, sR, sG, sB);
    let v: number;
    if (d <= half) v = 255;
    else if (d >= tol) v = 0;
    else {
      const t = (tol - d) / denom; // 1 at half, 0 at tol
      v = Math.round(255 * t);
      if (v < 0) v = 0;
      else if (v > 255) v = 255;
    }
    mask[p] = v;
  }
}
